/** @jsx isolateComponent */
import { useRef, useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Animation,
  Colors,
  Heading,
  Icon,
  Link,
  Shadows,
  Text,
  Typography,
} from '@zapier/design-system';

import { SizeType } from '../../../types';
import { rebrandFocusCss } from '../../../styles';

type ConversationLinkProps = {
  href: string;
  title: string;
  snippet?: string;
  size: SizeType;
};

const titleCss = (isActive: boolean) => css`
  ${Typography.Body4}
  color: ${Colors[isActive ? 'UiPrimaryStrongest' : 'PrimeBlack']};
`;

const contentCss = (size: SizeType) => css`
  display: block;
  border: 1px solid ${Colors.GrayWarm4};
  border-radius: 10px;
  background-color: ${Colors.PrimeWhite};
  padding: ${size === 'small' ? '10px' : '20px 28px'};
  display: grid;
  grid-row-gap: 5px;
  text-decoration: none;
  transition-property: shadow;
  transition-timing-function: ${Animation.transitionTimingFunction};
  transition-duration: ${Animation.transitionDuration};
  &:hover,
  &:focus {
    box-shadow: ${Shadows.elevation10};
  }
  ${rebrandFocusCss}
`;

const readMoreCss = css`
  font-weight: 700;
  display: grid;
  align-items: center;
  grid-template-columns: max-content auto;
  column-gap: 5px;
`;

const removeTextUnderlineCss = {
  'text-decoration': 'none',
};

/** Renders the content for the community-step in the form. */
const ConversationLink = ({
  href,
  title,
  size,
  snippet,
}: ConversationLinkProps) => {
  const [isActive, setIsActive] = useState(false);

  const onBlur = useRef(() => setIsActive(false)).current;
  const onFocus = useRef(() => setIsActive(true)).current;
  const onMouseEnter = useRef(() => setIsActive(true)).current;
  const onMouseLeave = useRef(() => setIsActive(false)).current;

  return (
    <Link
      css={contentCss(size)}
      href={href}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={removeTextUnderlineCss}
      target="_blank"
    >
      <Heading css={titleCss(isActive)}>{title}</Heading>
      {snippet && <Text tag="p">{snippet}</Text>}
      <span css={readMoreCss}>
        Read more <Icon name="arrowOffsite" size={18} />
      </span>
    </Link>
  );
};

export default ConversationLink;
